import React, { useMemo } from 'react'
import { Row, Col } from 'reactstrap'

import ChartCard from '../charts/ChartCard'
import DataTable from '../charts/DataTable'
import HorizontalStackedBar from '../charts/HorizontalStackedBar'
import StatTile, { StatTileRow } from '../charts/StatTile'
import TrendLine from '../charts/TrendLine'
import {
    CLAIM_TYPES,
    buildClaimsByBranch,
    buildClaimsByUser,
    buildClaimsKpis,
    buildClaimsTrend,
} from '../activityReport'

/**
 * Claims tab.
 *
 * Colour carries the claim type here -- liability blue, subro orange -- and
 * nothing else. Two series is comfortable for every reader, and it frees branch
 * and user to be carried by position instead of a ninth hue.
 *
 * Four views, each answering a different question, rather than six variations of
 * the same stacked bar:
 *   how many, and trending which way   -> stat tiles + trend line
 *   who is carrying the work           -> handler workload
 *   where the claims come from         -> branch mix
 */
const ClaimsTab = ({ rows, activeView, startDate, endDate }) => {
    const { kpis, trend, byUser, byBranch } = useMemo(
        () => ({
            kpis: buildClaimsKpis(rows, startDate, endDate),
            trend: buildClaimsTrend(rows, startDate, endDate, 'created'),
            byUser: buildClaimsByUser(rows),
            byBranch: buildClaimsByBranch(rows, 'created'),
        }),
        [rows, startDate, endDate]
    )

    const isTable = activeView === 'table'
    const hasClaims = byUser.data.length > 0

    // Handler workload: created and updated as two stacks per person, each split
    // by claim type. Position separates the two modes, colour the claim type.
    const workloadSeries = [
        ...CLAIM_TYPES.map((type) => ({
            key: `created_${type.key}`,
            label: `${type.label} created`,
            color: type.color,
            stack: 'created',
        })),
        ...CLAIM_TYPES.map((type) => ({
            key: `updated_${type.key}`,
            label: `${type.label} updated`,
            color: type.color,
            stack: 'updated',
        })),
    ]

    // Two colours, four series: the legend names the claim types, and the
    // created/updated split is carried by the two bars per person.
    const workloadLegend = CLAIM_TYPES.map((type) => ({
        value: type.label,
        type: 'square',
        color: type.color,
        id: type.key,
    }))

    const userTableRows = byUser.data.map((record) => ({
        name: record.userName,
        total: record.total,
        values: workloadSeries.reduce((acc, item) => ({ ...acc, [item.key]: record[item.key] || 0 }), {}),
    }))

    const branchTableRows = byBranch.data.map((record) => ({
        name: record.branchName,
        total: record.total,
        values: CLAIM_TYPES.reduce((acc, type) => ({ ...acc, [type.key]: record[type.key] || 0 }), {}),
    }))

    const trendTableRows = trend.data
        .filter((record) => CLAIM_TYPES.some((type) => record[type.key]))
        .map((record) => ({
            name: record.label,
            total: CLAIM_TYPES.reduce((sum, type) => sum + (record[type.key] || 0), 0),
            values: CLAIM_TYPES.reduce((acc, type) => ({ ...acc, [type.key]: record[type.key] || 0 }), {}),
        }))

    return (
        <>
            <StatTileRow>
                <StatTile
                    label="Liability opened"
                    value={kpis.liability.total}
                    caption="New liability claims"
                    spark={kpis.liability.spark}
                    tone="liability"
                    accent="liability"
                />
                <StatTile
                    label="Subro opened"
                    value={kpis.subro.total}
                    caption="New subrogation claims"
                    spark={kpis.subro.spark}
                    tone="subro"
                    accent="subro"
                />
                <StatTile
                    label="Update events"
                    value={kpis.updates.total}
                    caption="Edits across both claim types"
                    spark={kpis.updates.spark}
                    tone="muted"
                />
                <StatTile label="Top handler" value={kpis.topUser.name} caption={`${kpis.topUser.count.toLocaleString()} claim actions`} />
            </StatTileRow>

            <Row>
                <Col xs="12">
                    <ChartCard
                        title="Claims Opened Over Time"
                        subtitle="New claims per day, by type"
                        isEmpty={!hasClaims}
                    >
                        {isTable ? (
                            <DataTable rows={trendTableRows} series={CLAIM_TYPES} rowHeader="Day" />
                        ) : (
                            <TrendLine data={trend.data} series={trend.series} height={300} />
                        )}
                    </ChartCard>
                </Col>
            </Row>

            <Row>
                <Col xs="12" xl={isTable ? 12 : 7}>
                    <ChartCard
                        title="Handler Workload"
                        subtitle="Created and updated per person — creations count claims, updates count edit events"
                        isEmpty={!hasClaims}
                    >
                        {isTable ? (
                            <DataTable rows={userTableRows} series={workloadSeries} />
                        ) : (
                            <HorizontalStackedBar
                                data={byUser.data}
                                series={workloadSeries}
                                yKey="userName"
                                legendPayload={workloadLegend}
                                showTotals={false}
                                unit="users"
                            />
                        )}
                    </ChartCard>
                </Col>
                <Col xs="12" xl={isTable ? 12 : 5}>
                    <ChartCard
                        title="Where Claims Come From"
                        subtitle="Claims opened per branch"
                        isEmpty={!byBranch.data.length}
                    >
                        {isTable ? (
                            <DataTable rows={branchTableRows} series={CLAIM_TYPES} rowHeader="Branch" />
                        ) : (
                            <HorizontalStackedBar data={byBranch.data} series={CLAIM_TYPES} yKey="branchName" unit="branches" />
                        )}
                    </ChartCard>
                </Col>
            </Row>
        </>
    )
}

export default ClaimsTab
